/**
 * hostRouter — surface-routing helper for SignVoice.
 *
 * Two deploy topologies are supported by the same bundle:
 *
 *   1. UNIFIED (Phase 7+ default) — one host serves both surfaces.
 *      Consumer + Talent Connect share the same origin. HostGuard is a no-op;
 *      client-side <Routes> handles everything.
 *
 *   2. SPLIT-DOMAIN (Phase 6+ dormant path) — two sub-domains:
 *      signvoice.<base>     → consumer
 *      talentconnect.<base> → Talent Connect
 *      Activated ONLY when BOTH env vars below are set to DIFFERENT
 *      hostnames. HostGuard then cross-origin redirects mismatched routes.
 *
 * Build-time env vars:
 *   REACT_APP_CONSUMER_HOST=signvoice.tu-dominio.com
 *   REACT_APP_TALENTCONNECT_HOST=talentconnect.tu-dominio.com
 */
const CONSUMER_HOST = process.env.REACT_APP_CONSUMER_HOST || '';
const TALENTCONNECT_HOST = process.env.REACT_APP_TALENTCONNECT_HOST || '';

/**
 * Split-domain deploy is active only if BOTH env vars are set AND they
 * point to different hostnames. Every other configuration (missing vars,
 * same value, dev preview) means unified — no cross-origin redirects.
 */
export function isSplitDomain() {
  if (!CONSUMER_HOST || !TALENTCONNECT_HOST) return false;
  return CONSUMER_HOST !== TALENTCONNECT_HOST;
}

/** @returns {'consumer' | 'talentconnect' | 'dev'} */
export function getHostMode() {
  if (typeof window === 'undefined') return 'dev';
  if (!isSplitDomain()) return 'dev';
  const host = window.location.hostname;
  if (host === TALENTCONNECT_HOST) return 'talentconnect';
  if (host === CONSUMER_HOST) return 'consumer';
  return 'dev';
}

/** Build a cross-origin URL when the target route lives on a different host. */
export function buildCrossDomainUrl(targetMode, path) {
  if (targetMode === 'consumer' && CONSUMER_HOST) {
    return `https://${CONSUMER_HOST}${path}`;
  }
  if (targetMode === 'talentconnect' && TALENTCONNECT_HOST) {
    return `https://${TALENTCONNECT_HOST}${path}`;
  }
  return path;
}

/** Decide which mode a given pathname "belongs to". */
export function modeForPath(pathname) {
  if (pathname.startsWith('/talent-connect') || pathname.startsWith('/staffing')) {
    return 'talentconnect';
  }
  if (pathname === '/privacy' || pathname === '/terms') {
    return null; // shared — both surfaces serve these
  }
  return 'consumer';
}

export const HOST_CONFIG = { CONSUMER_HOST, TALENTCONNECT_HOST };
